import React from 'react';
import { X, Shield, Lock, Eye, Database, Globe, AlertCircle, Sparkles, Users, FileText } from 'lucide-react';

const PrivacyPolicy = ({ onClose, embedded = false, standalone = false }) => {
  const Container = embedded ? 'div' : 'div';
  const contentClass = standalone
    ? ''
    : embedded
    ? 'p-6'
    : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';

  return (
    <div className={contentClass}>
      <div className={`bg-white rounded-2xl max-w-4xl w-full ${standalone ? '' : embedded ? '' : 'max-h-[90vh] overflow-hidden flex flex-col shadow-2xl'}`}>
        {/* Header */}
        {!embedded && !standalone && (
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Privacy Policy</h2>
                <p className="text-sm text-gray-600">Last updated: May 6, 2026</p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            )}
          </div>
        )}

        {/* Standalone Header */}
        {standalone && (
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Privacy Policy</h1>
                <p className="text-sm text-gray-600 mt-1">Last updated: May 6, 2026</p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className={`${standalone ? '' : embedded ? '' : 'overflow-y-auto flex-1'} p-6`}>
          {/* Quick Summary Banner */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-500 p-5 rounded-r-lg mb-6">
            <h3 className="font-bold text-gray-900 mb-3 text-lg">Quick Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <p className="font-semibold text-gray-800">What we collect:</p>
                <p className="text-gray-700">Your name, email, financial goals, portfolio data, and usage information</p>
              </div>
              <div>
                <p className="font-semibold text-gray-800">Why we collect it:</p>
                <p className="text-gray-700">To provide investment planning tools and AI-powered insights</p>
              </div>
              <div>
                <p className="font-semibold text-gray-800">How we use it:</p>
                <p className="text-gray-700">AI analysis, portfolio tracking, retirement planning, app improvements</p>
              </div>
              <div>
                <p className="font-semibold text-gray-800">Who we share with:</p>
                <p className="text-gray-700">Only essential service providers (Stripe, Firebase)</p>
              </div>
            </div>
            <div className="mt-3 p-3 bg-white rounded border border-green-200">
              <p className="font-bold text-green-900 mb-1">We NEVER:</p>
              <p className="text-sm text-green-800">Sell your data to third parties or use it for advertising</p>
            </div>
          </div>

          <div className="prose prose-sm max-w-none space-y-6 text-gray-700">
            {/* Section 1 - Introduction */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">1. Introduction</h3>
              <p>
                Welcome to PlanLens ("we," "us," or "our"). This Privacy Policy explains how PlanLens
                collects, uses, shares, and protects your personal information when you use our investment
                and wealth planning software application (the "Service").
              </p>

              <div className="bg-gray-50 p-4 rounded-lg mt-3">
                <p className="font-semibold text-gray-900 mb-2">Service Details:</p>
                <ul className="list-none space-y-1 text-sm">
                  <li><strong>Name:</strong> PlanLens</li>
                  <li><strong>Contact:</strong> support@planlens.ai</li>
                  <li><strong>Website:</strong> https://www.planlens.ai</li>
                </ul>
              </div>

              <p className="mt-3">We are committed to protecting your privacy and complying with:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>UK General Data Protection Regulation (UK GDPR)</li>
                <li>Data Protection Act 2018</li>
                <li>Privacy and Electronic Communications Regulations (PECR)</li>
                <li>California Consumer Privacy Act (CCPA) for US users</li>
              </ul>
              <p className="mt-3">
                By using PlanLens, you agree to this Privacy Policy. If you don't agree, please don't use our Service.
              </p>
            </section>

            {/* Section 2 - Information We Collect */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">2. Information We Collect</h3>

              <div className="flex items-start space-x-3 bg-blue-50 p-4 rounded-lg mb-4">
                <Database className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-900 mb-1">2.1 Information You Provide Directly</p>
                </div>
              </div>

              <p className="font-semibold text-gray-900">Account Information:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                <li>Full name</li>
                <li>Email address</li>
                <li>Password (stored as encrypted hash only)</li>
                <li>Country/region</li>
                <li>Account preferences</li>
              </ul>

              <p className="font-semibold text-gray-900">Financial Planning Data:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                <li>Current portfolio holdings (stocks, bonds, ETFs, cryptocurrency, cash, commodities)</li>
                <li>Retirement goals and timelines</li>
                <li>Income and expense estimates</li>
                <li>Asset allocation preferences</li>
                <li>Investment objectives</li>
                <li>Risk tolerance</li>
                <li>Financial questions and queries</li>
              </ul>

              <p className="font-semibold text-gray-900">Payment Information:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                <li>Billing name</li>
                <li>Email for receipts</li>
                <li>Payment card details (processed by Stripe - we never store your card numbers)</li>
              </ul>

              <p className="font-semibold text-gray-900 mt-4">2.2 Information Collected Automatically</p>
              <p className="mb-2">We automatically collect usage and technical data:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Pages visited and features used</li>
                <li>Device type, browser, and operating system</li>
                <li>IP address and approximate location</li>
                <li>Session duration and click patterns</li>
                <li>Error logs and performance metrics</li>
              </ul>
            </section>

            {/* Section 3 - Legal Basis */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">3. Legal Basis for Processing (UK GDPR)</h3>
              <p className="mb-3">Under UK GDPR Article 6, we process your personal data based on:</p>

              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300 text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Data Type</th>
                      <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Legal Basis</th>
                      <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-3 py-2">Account Data</td>
                      <td className="border border-gray-300 px-3 py-2">Contract</td>
                      <td className="border border-gray-300 px-3 py-2">Necessary to provide the Service</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-3 py-2">Financial Planning Data</td>
                      <td className="border border-gray-300 px-3 py-2">Contract</td>
                      <td className="border border-gray-300 px-3 py-2">You provide this to use our planning tools</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-3 py-2">Payment Data</td>
                      <td className="border border-gray-300 px-3 py-2">Contract</td>
                      <td className="border border-gray-300 px-3 py-2">Process subscription payments</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-3 py-2">Usage Analytics</td>
                      <td className="border border-gray-300 px-3 py-2">Legitimate Interest</td>
                      <td className="border border-gray-300 px-3 py-2">Improve functionality and user experience</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-3 py-2">Marketing Emails</td>
                      <td className="border border-gray-300 px-3 py-2">Consent</td>
                      <td className="border border-gray-300 px-3 py-2">You can opt out anytime</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 4 - AI Features */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">4. AI-Powered Features and Data Processing</h3>

              <div className="flex items-start space-x-3 bg-purple-50 p-4 rounded-lg mb-4">
                <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-purple-900 mb-1">4.1 How We Use AI</p>
                  <p className="text-sm text-purple-800">
                    PlanLens uses artificial intelligence to provide personalized financial insights.
                  </p>
                </div>
              </div>

              <p className="font-semibold text-gray-900">What AI Analyzes:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                <li>Your portfolio holdings and allocations</li>
                <li>Your retirement goals and timelines</li>
                <li>Financial scenarios and questions</li>
                <li>Historical market data and investment patterns</li>
                <li>Risk tolerance and investment preferences</li>
              </ul>

              <p className="font-semibold text-gray-900">AI Features We Provide:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                <li>Portfolio analysis and recommendations</li>
                <li>Retirement planning projections (including Monte Carlo simulations)</li>
                <li>Asset allocation suggestions</li>
                <li>Risk assessment and scenario modeling</li>
              </ul>

              <p className="font-semibold text-gray-900 mt-4">4.2 AI Data Usage</p>
              <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-r-lg mb-3">
                <p className="font-semibold text-green-900 mb-1">Data Protection:</p>
                <ul className="text-sm text-green-800 space-y-1">
                  <li>✓ Personal identifiers (name, email) are NOT sent to AI providers</li>
                  <li>✓ Only financial data is processed (portfolio, amounts, goals)</li>
                  <li>✓ Your data is NOT used to train third-party AI models</li>
                  <li>✓ We do NOT sell your data to AI companies</li>
                </ul>
              </div>

              <p className="font-semibold text-gray-900 mt-4">4.3 AI Data Retention</p>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-lg mb-3">
                <p className="text-amber-900">
                  <strong>Important:</strong> We do not retain any AI-generated input or output data.
                  All AI responses are produced on a temporary, session-only basis and are never stored in our systems.
                </p>
              </div>

              <p className="font-semibold text-gray-900 mt-4">4.4 AI Disclaimers</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                <li><strong>Not Financial Advice:</strong> AI insights are informational only, not personalized advice</li>
                <li><strong>AI Can Err:</strong> Always verify information independently</li>
                <li><strong>No Guarantees:</strong> Projections are estimates, not predictions</li>
              </ul>
            </section>

            {/* Section 5 - How We Use Your Information */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">5. How We Use Your Information</h3>

              <p className="font-semibold text-gray-900">5.1 Provide and Improve the Service</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                <li>Create and manage your account</li>
                <li>Store portfolio and financial goals</li>
                <li>Generate AI-powered insights</li>
                <li>Provide retirement planning projections</li>
                <li>Analyze usage to improve features</li>
                <li>Fix bugs and optimize performance</li>
              </ul>

              <p className="font-semibold text-gray-900">5.2 Communications</p>
              <p className="mb-2"><strong>Service-Related (Cannot opt out):</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-2 text-sm">
                <li>Account verification and security alerts</li>
                <li>Billing confirmations and receipts</li>
                <li>Critical service announcements</li>
              </ul>
              <p className="mb-2"><strong>Marketing (Can opt out):</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-2 text-sm">
                <li>Product updates and tips</li>
                <li>Educational content</li>
              </ul>

              <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-r-lg mt-4">
                <p className="font-bold text-red-900 mb-1">What We NEVER Do:</p>
                <ul className="text-sm text-red-800 space-y-1">
                  <li>❌ Sell your personal data</li>
                  <li>❌ Use your data for targeted advertising</li>
                  <li>❌ Share portfolio details with third parties (except service providers)</li>
                  <li>❌ Train third-party AI models with your identifiable data</li>
                </ul>
              </div>
            </section>

            {/* Section 6 - How We Share Information */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">6. How We Share Your Information</h3>
              <p className="mb-3">We only share your data when necessary to operate the Service:</p>

              <p className="font-semibold text-gray-900">6.1 Service Providers (Data Processors)</p>

              <div className="space-y-3 ml-4 mb-4">
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <p className="font-semibold text-gray-900">Stripe</p>
                  <p className="text-sm"><strong>Purpose:</strong> Payment processing</p>
                  <p className="text-sm"><strong>Data shared:</strong> Billing name, email, payment card details</p>
                  <p className="text-sm"><strong>Privacy Policy:</strong> https://stripe.com/privacy</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <p className="font-semibold text-gray-900">Firebase / Google Cloud Platform</p>
                  <p className="text-sm"><strong>Purpose:</strong> Authentication, database hosting, backend services</p>
                  <p className="text-sm"><strong>Data shared:</strong> Account data, portfolio data, usage logs</p>
                  <p className="text-sm"><strong>Privacy Policy:</strong> https://firebase.google.com/support/privacy</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <p className="font-semibold text-gray-900">Google Gemini (AI Services)</p>
                  <p className="text-sm"><strong>Purpose:</strong> AI-powered financial insights</p>
                  <p className="text-sm"><strong>Data shared:</strong> Anonymized financial queries (NO names/emails)</p>
                  <p className="text-sm"><strong>Training:</strong> Your data is NOT used to train their models</p>
                  <p className="text-sm"><strong>Privacy Policy:</strong> https://support.google.com/gemini/answer/13594961</p>
                </div>
              </div>

              <p className="font-semibold text-gray-900">6.2 Legal Requirements</p>
              <p className="text-sm mb-3">
                We may disclose information if required by law, court order, or to protect our rights and safety.
              </p>

              <p className="font-semibold text-gray-900">6.3 Business Transfers</p>
              <p className="text-sm mb-3">
                If PlanLens is acquired or merges, your data may transfer to the new owner.
                You'll be notified 30 days in advance.
              </p>
            </section>

            {/* Section 7 - International Data Transfers */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">7. International Data Transfers</h3>

              <p className="font-semibold text-gray-900">7.1 Where Your Data Goes</p>
              <p className="mb-2"><strong>UK Users:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3 text-sm">
                <li>✅ Primary storage: EU/UK Firebase servers</li>
                <li>⚠️ Backup storage: US Firebase servers (for redundancy)</li>
                <li>⚠️ Payment processing: Stripe (US company, processes in EU and US)</li>
                <li>⚠️ AI processing: Google Gemini (US)</li>
              </ul>

              <p className="font-semibold text-gray-900">7.2 Safeguards for UK/EU → US Transfers</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                <li>Standard Contractual Clauses (SCCs)</li>
                <li>Encryption in transit (TLS 1.3) and at rest (AES-256)</li>
                <li>Access controls and monitoring</li>
              </ul>
            </section>

            {/* Section 8 - Cookies */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">8. Cookies and Tracking Technologies</h3>

              <p className="font-semibold text-gray-900">Essential Cookies (Always Active)</p>
              <div className="overflow-x-auto mb-3">
                <table className="min-w-full border border-gray-300 text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-3 py-2 text-left">Cookie</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">Purpose</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-3 py-2">auth_token</td>
                      <td className="border border-gray-300 px-3 py-2">Authentication</td>
                      <td className="border border-gray-300 px-3 py-2">30 days</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-3 py-2">session_id</td>
                      <td className="border border-gray-300 px-3 py-2">Session management</td>
                      <td className="border border-gray-300 px-3 py-2">Session</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="font-semibold text-gray-900">Analytics Cookies (Optional)</p>
              <p className="text-sm mb-2">Help us understand usage patterns. You can opt out in Account Settings.</p>
            </section>

            {/* Section 9 - Data Security */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">9. Data Security</h3>

              <div className="flex items-start space-x-3 bg-indigo-50 p-4 rounded-lg mb-4">
                <Lock className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-indigo-900 mb-1">Security Measures</p>
                  <p className="text-sm text-indigo-800">
                    We implement industry-standard security to protect your data.
                  </p>
                </div>
              </div>

              <p className="font-semibold text-gray-900">Technical Security:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                <li><strong>Encryption:</strong> TLS 1.3 in transit, AES-256 at rest</li>
                <li><strong>Passwords:</strong> bcrypt hashing (never stored in plain text)</li>
                <li><strong>Access Controls:</strong> Role-based permissions, MFA for admins</li>
                <li><strong>Monitoring:</strong> Real-time security monitoring and threat detection</li>
              </ul>

              <p className="font-semibold text-gray-900">Data Breach Notification:</p>
              <p className="text-sm">
                If a breach occurs, we'll notify the ICO within 72 hours and affected users via email.
              </p>
            </section>

            {/* Section 10 - Data Retention */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">10. Data Retention and Deletion</h3>

              <p className="font-semibold text-gray-900">10.1 Retention Periods</p>
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full border border-gray-300 text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-3 py-2 text-left">Data Type</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">Retention Period</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-3 py-2">Account Data</td>
                      <td className="border border-gray-300 px-3 py-2">Active + 30 days</td>
                      <td className="border border-gray-300 px-3 py-2">Provide service</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-3 py-2">Financial Planning Data</td>
                      <td className="border border-gray-300 px-3 py-2">Active + 30 days</td>
                      <td className="border border-gray-300 px-3 py-2">Core features</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-3 py-2">AI Analysis Data</td>
                      <td className="border border-gray-300 px-3 py-2">Session only</td>
                      <td className="border border-gray-300 px-3 py-2">Never stored</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-3 py-2">Payment Records</td>
                      <td className="border border-gray-300 px-3 py-2">7 years</td>
                      <td className="border border-gray-300 px-3 py-2">UK tax law (HMRC)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="font-semibold text-gray-900">10.2 How to Delete Your Account</p>
              <div className="bg-gray-50 p-3 rounded-lg mb-3">
                <p className="font-semibold text-gray-900 mb-2">Option 1: Self-Service</p>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>Log in to PlanLens</li>
                  <li>Go to Account Settings</li>
                  <li>Click "Delete Account"</li>
                  <li>Confirm deletion (cannot be undone)</li>
                </ol>
                <p className="font-semibold text-gray-900 mt-3 mb-2">Option 2: Email Request</p>
                <p className="text-sm">Email Support@planlens.ai with subject "Delete My Account"</p>
              </div>

              <p className="text-sm text-gray-600">
                All personal data will be permanently deleted within 30 days. Billing records are kept for 7 years (UK tax law).
              </p>
            </section>

            {/* Section 11 - Your Privacy Rights */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">11. Your Privacy Rights</h3>

              <p className="font-semibold text-gray-900">11.1 Rights Under UK GDPR</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3 text-sm">
                <li><strong>Right to Access:</strong> Request a copy of your data (free, 30-day response)</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data</li>
                <li><strong>Right to Data Portability:</strong> Receive data in machine-readable format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interest</li>
                <li><strong>Right to Lodge a Complaint:</strong> Complain to the ICO (https://ico.org.uk)</li>
              </ul>

              <p className="font-semibold text-gray-900">11.2 Rights Under CCPA (California Users)</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3 text-sm">
                <li><strong>Right to Know:</strong> What personal information we collect and why</li>
                <li><strong>Right to Delete:</strong> Request deletion of your data</li>
                <li><strong>Right to Opt-Out:</strong> We do NOT sell your data (never have, never will)</li>
                <li><strong>Right to Non-Discrimination:</strong> Same service quality regardless</li>
              </ul>

              <p className="font-semibold text-gray-900">11.3 How to Exercise Your Rights</p>
              <p className="text-sm">
                Email: <strong>support@planlens.ai</strong> with subject line indicating your request
                (e.g., "Subject Access Request", "Delete My Account", "Data Portability Request").
              </p>
            </section>

            {/* Section 12 - Children's Privacy */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">12. Children's Privacy</h3>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-lg">
                <p className="font-semibold text-amber-900 mb-1">Age Requirement: 18+</p>
                <p className="text-sm text-amber-800">
                  PlanLens is intended for users aged 18 and older. We do not knowingly collect information
                  from anyone under 18. If you believe your child has provided us with data, email us immediately
                  at support@planlens.ai for deletion.
                </p>
              </div>
            </section>

            {/* Section 13 - Changes to Policy */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">13. Changes to This Privacy Policy</h3>
              <p className="text-sm mb-2">
                We may update this Privacy Policy from time to time. For material changes, we'll provide
                30 days' notice via email and in-app notification.
              </p>
              <p className="text-sm">
                Continued use after changes = acceptance of new policy.
              </p>
            </section>

            {/* Section 14 - Contact Us */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">14. Contact Us</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900 mb-2">PlanLens - Data Protection</p>
                <p className="text-sm"><strong>Email:</strong> support@planlens.ai</p>
                <p className="text-sm"><strong>Response Time:</strong> 48 hours for acknowledgment</p>
                <div className="mt-3 pt-3 border-t border-gray-300">
                  <p className="text-sm text-gray-700">
                    <strong>UK Users:</strong> Lodge complaints with the Information Commissioner's Office (ICO)
                    at https://ico.org.uk or call 0303 123 1113
                  </p>
                  <p className="text-sm text-gray-700 mt-1">
                    <strong>California Users:</strong> Contact California Attorney General at https://oag.ca.gov
                  </p>
                </div>
              </div>
            </section>

            {/* Summary */}
            <section className="bg-gradient-to-r from-green-50 to-blue-50 p-5 rounded-lg border-2 border-green-300">
              <p className="font-bold text-gray-900 mb-3 text-lg">Summary of Key Points</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-800">
                <div>✅ <strong>What we collect:</strong> Name, email, financial goals, portfolio data</div>
                <div>✅ <strong>Why:</strong> Provide planning tools, AI insights, improve Service</div>
                <div>✅ <strong>AI:</strong> Analyzes your data; NOT used to train third-party models</div>
                <div>✅ <strong>Sharing:</strong> Only essential providers (Stripe, Firebase, Gemini)</div>
                <div>✅ <strong>Selling:</strong> We NEVER sell your data</div>
                <div>✅ <strong>Retention:</strong> Active account + 30 days (billing: 7 years)</div>
                <div>✅ <strong>Security:</strong> Encryption, access controls, monitoring</div>
                <div>✅ <strong>Your rights:</strong> Access, delete, export, correct, object</div>
                <div>✅ <strong>Age:</strong> 18+ only</div>
                <div>✅ <strong>Contact:</strong> Support@planlens.ai</div>
              </div>
            </section>

            {/* Version Info */}
            <section className="text-center text-sm text-gray-600 pt-4 border-t border-gray-200">
              <p><strong>Effective Date:</strong> May 6, 2026</p>
              <p><strong>Version:</strong> 1.0</p>
              <p className="mt-2 italic">Thank you for trusting PlanLens with your financial planning journey.</p>
            </section>
          </div>
        </div>

        {/* Footer */}
        {!embedded && onClose && (
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="w-full py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivacyPolicy;
